import useEForm from "@/hooks/use-eform/index";
import BaseEForm from "./BaseEform";
import EFormAccordion from "./EFormAccordion";
import classNames from 'classnames';
import { Fragment } from "react";
import { ReactComponent as EFormCreateBtn } from "@/assets/svgs/eformv2-create-btn.svg";
import { ReactComponent as DeleteIcon } from "@/assets/svgs/trash.svg";
import { ReactComponent as CopyIcon } from "@/assets/svgs/icon-document-duplicate.svg";
import PropTypes from 'prop-types';
import { snakeCase } from "lodash";
import { useFeature } from "@/hooks/use-feature/index";
import { featureConstant } from "@/constants/configuration/feature";
import { parentPropsToPath, isPathLocked } from "@/utils/eform-schema-paths";
import Callout from "@/componentsDS/Callout/Callout";
import { CalloutType, CalloutVariant } from "@/componentsDS/Callout/Callout.type";
import InfoIcon, { resolveDescription } from "./InfoIcon";

const ObjectFieldRenderer = ({ testId, field, fieldPath, isRequired, fieldParentProps, level, payload, setPayload, formHandlers, pathIndex = 0, handleAddAccordion, handleRemoveAccordion, isReadOnly, previousPayload, changedFields }) => {
    const { checkIfNested, getFieldValue } = useEForm();
    let obtainedFieldValues = getFieldValue(payload, fieldParentProps);
    let isCreated = typeof obtainedFieldValues === 'object' && obtainedFieldValues !== null && !Array.isArray(obtainedFieldValues);

    // The "Copy from last approved submission" button is rendered next to a section
    // title when the section path is in `formHandlers.copyFromLastSections`. It is
    // disabled when the section is read-only or its path is locked.
    const isSubmittalsEnabled = useFeature(featureConstant.SUBMITTALS);
    const sectionPath = parentPropsToPath(fieldParentProps);
    const isSectionLocked = isSubmittalsEnabled
        && isPathLocked(sectionPath, formHandlers?.lockedPaths);
    // Highlight sections marked as currently-editable in a partially-locked form.
    // Driven by schema's `x-uioptions.editable-statuses`; see `getEditableHighlightPaths`.
    // Gated on SUBMITTALS feature, matching `isSectionLocked` above.
    const isSectionEditableHighlight = isSubmittalsEnabled
        && !!(formHandlers?.editableHighlightPaths instanceof Set
        && formHandlers.editableHighlightPaths.has(sectionPath));
    const showCopyFromLast = isSubmittalsEnabled
        && formHandlers?.copyFromLastSections instanceof Set
        && formHandlers.copyFromLastSections.has(sectionPath);
    const isCopyDisabled = isReadOnly || isSectionLocked;
    const copyFromLastBtn = showCopyFromLast ? (
        <button
            type="button"
            className="eformv2__copy-from-last"
            data-testid={`${testId}__copy-from-last`}
            disabled={isCopyDisabled}
            onClick={() => !isCopyDisabled && formHandlers.onCopyFromLast?.(sectionPath)}
        >
            <CopyIcon />
            <span>Copy from last approved submission</span>
        </button>
    ) : null;

    function handleDeleteAccordion(parentProps, index) {
        if (isReadOnly) return;
        handleRemoveAccordion(parentProps, index)
    }

    const handleCreateAccordion = (parentProps, fieldSchema) => {
        let updatedFieldSchema = { ...fieldSchema };
        if (Array.isArray(updatedFieldSchema.type)) {
            const nonNullType = updatedFieldSchema.type.find(f => f !== 'null');
            updatedFieldSchema.type = nonNullType;
        }
        handleAddAccordion(parentProps, updatedFieldSchema, true);
    }

    if (level === 1 && !isRequired) {
        return (
            <>
                {!isCreated && !isReadOnly && !isSectionLocked && (
                    <div className="eformv2__root-object-container" data-testid={`${testId}__create-accordion`}>
                        <div className="eformv2-accordion__container eformv2-accordion__container-style--root-level-action" onClick={() => handleCreateAccordion(fieldParentProps, field)}>
                            <div className="eformv2-accordion__action-icon"><EFormCreateBtn /></div>
                            <div>
                                <div className="eformv2-accordion__main-title--action">
                                    {`Create ${field.title}`}
                                    <InfoIcon content={field.description} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {isCreated && (
                    <div className={`eformv2__root-object-container`} data-testid={`${testId}__accordion`}>
                        <div className={`eformv2__root-object`}>
                            <div className="eformv2__object-title">
                                {field.title}
                                <InfoIcon content={field.description} />
                            </div>
                            {!isReadOnly && !isSectionLocked && (
                                <div
                                    className={`eformv2-accordion__action-delete ${isReadOnly ? "disabled" : ""} is-root-object`}
                                    data-testid={`delete-${testId}`}
                                    onClick={() => handleDeleteAccordion(fieldParentProps, pathIndex)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleDeleteAccordion(fieldParentProps, pathIndex)}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <div className={`eformv2-accordion__action-icon eformv2-accordion__delete-icon`}><DeleteIcon /></div>
                                    <div className={`eformv2-accordion__delete-text`}>Delete</div>
                                </div>
                            )}
                        </div>
                        <div style={{ marginLeft: '25px' }} >
                            <FormRenderer schema={field} path={fieldPath} level={level + 1} parentProps={fieldParentProps} payload={payload} setPayload={setPayload} pathIndex={pathIndex} isReadOnly={isReadOnly} previousPayload={previousPayload} changedFields={changedFields} {...formHandlers} />
                        </div>
                    </div>
                )}
            </>
        );
    }

    if (level === 1 && isRequired) {
        const fieldsWrapperClass = classNames('eformv2__root-object-fields', {
            'eformv2__root-object-fields--editable-highlight': isSectionEditableHighlight,
        });
        return (
            <div className="eformv2__root-object-container" data-testid={`${testId}__create-accordion`}>
                <div className="eformv2__object-title-row">
                    <div className="eformv2__object-title">
                        <span className="eformv2-label-asterisk" data-testid="eform-label-asterisk">*</span>
                        {field.title}
                        <InfoIcon content={field.description} />
                    </div>
                    {copyFromLastBtn}
                </div>
                <div className={fieldsWrapperClass} style={{ marginLeft: '25px' }} >
                    <FormRenderer schema={field} path={fieldPath} level={level + 1} parentProps={fieldParentProps} payload={payload} setPayload={setPayload} pathIndex={pathIndex} isReadOnly={isReadOnly} previousPayload={previousPayload} changedFields={changedFields} {...formHandlers} />
                </div>
                {isSectionEditableHighlight && resolveDescription(field.description) && (
                    <Callout
                        type={CalloutType.INFO}
                        variant={CalloutVariant.STANDARD}
                        message={resolveDescription(field.description)}
                        className="eformv2__root-object-container__editable-info"
                    />
                )}
            </div>
        );
    }

    if (level > 1) {
        let isNested = checkIfNested(field);
        return (
            <div key={`${fieldPath}-object`} className={classNames('eformv2-object-accordion-container', { 'is-nested': isNested }, { 'optional': !isRequired })}>
                <EFormAccordion testId={testId} title={field.title} subTitle={fieldPath} level={level} field={field} isRequired={isRequired} fieldType={'object'} handleRemoveFunc={handleDeleteAccordion} handleAddFunc={handleAddAccordion} parentProps={fieldParentProps} isCreated={isCreated} isReadOnly={isReadOnly}>
                    <FormRenderer schema={field} path={fieldPath} level={level + 1} parentProps={fieldParentProps} payload={payload} setPayload={setPayload} pathIndex={pathIndex} isReadOnly={isReadOnly} previousPayload={previousPayload} changedFields={changedFields} {...formHandlers} />
                </EFormAccordion>
            </div>
        );
    }
};

const FormRenderer = ({ schema, path = '', level = 1, parentProps = [], setPayload, payload, pathIndex, isReadOnly = false, previousPayload, changedFields, selectedModelTerms, setSelectedModelTerms, isSubmitClicked = false, ...formHandlers }) => {
    const { getFieldValue, getFullJson, getNextPayload, checkIfNested } = useEForm();
    const isSubmittalsEnabled = useFeature(featureConstant.SUBMITTALS);

    if (!payload || payload.length === 0 || !schema) {
        return null;
    }

    const handleAddAccordion = (parentProps, fieldSchema, isObj = false, isDisabled = false) => {
        if (isDisabled) return;
        let fieldValue = getFieldValue(payload, parentProps) || [];
        fieldValue = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
        const arr = [...fieldValue, getFullJson(fieldSchema)];
        const nextPayload = getNextPayload(payload, parentProps, isObj ? arr[0] : arr);
        setPayload(nextPayload);
    };

    const handleRemoveAccordion = (parentProps, index) => {
        let updatedFieldValue = null;
        const fieldValue = getFieldValue(payload, parentProps);
        if (!fieldValue || fieldValue.length === 0) return;
        if (!Array.isArray(fieldValue)) {
            setPayload(getNextPayload(payload, parentProps, null));
            return;
        }
        updatedFieldValue = fieldValue.filter((_, i) => i !== index);
        let newValue = getNextPayload(payload, parentProps, updatedFieldValue.length > 0 ? updatedFieldValue : null);

        setPayload(newValue);
    };

    const renderFields = (properties, required, currentPath, parentProps, payload, level, isReadOnly) => {
        return Object.keys(properties).map((key) => {
            const field = properties[key];

            const visibilityRules = isSubmittalsEnabled && field['x-uioptions']?.['visibility-rules'];
            if (visibilityRules) {
                const conditionMet = visibilityRules.and.every(({ fieldKey, fieldValue }) => {
                    const siblingValue = getFieldValue(payload, [...parentProps, fieldKey]);
                    return Array.isArray(siblingValue)
                        ? siblingValue.includes(fieldValue)
                        : siblingValue === fieldValue;
                });
                if (!conditionMet) {
                    const currentValue = getFieldValue(payload, [...parentProps, key]);
                    if (currentValue !== null && currentValue !== undefined) {
                        setPayload(getNextPayload(payload, [...parentProps, key], null));
                    }
                    return null;
                }
            }

            const isRequired = required?.includes(key);
            const fieldPath = currentPath ? `${currentPath} / ${field.title}` : `${field.title}`;
            let fieldType = Array.isArray(field.type) ? field.type.find((d) => d !== 'null') : field.type;
            /** handle extended properties  */
            fieldType = field['x-uitype'] ?? fieldType;
            const fieldParentProps = [...parentProps, key];
            const ObjTestIdValue = snakeCase(key + '-' + level);

            if (fieldType === 'object' && field.properties) {
                return <ObjectFieldRenderer key={`${key}-${level}`} testId={ObjTestIdValue} field={field} fieldPath={fieldPath} isRequired={isRequired} fieldParentProps={fieldParentProps} level={level} payload={payload} setPayload={setPayload} formHandlers={formHandlers} pathIndex={pathIndex} handleAddAccordion={handleAddAccordion} handleRemoveAccordion={handleRemoveAccordion} isReadOnly={isReadOnly} previousPayload={previousPayload} changedFields={changedFields} />;
            }

            if (fieldType === 'array' && field.items?.properties) {
                return renderArrayFields(field, fieldPath, isRequired, fieldParentProps, level, isReadOnly);
            }

            return renderBaseFields(field, fieldPath, fieldType, isRequired, fieldParentProps, level, isReadOnly);
        });
    };

    const renderArrayFields = (field, fieldPath, isRequired, fieldParentProps, level, isReadOnly) => (
        <div key={`${fieldPath}-array`} className="eformv2__root-array">
            {renderArrayItems(field, fieldPath, fieldParentProps, isRequired, level, isReadOnly)}
        </div>
    );

    const renderArrayItems = (field, fieldPath, fieldParentProps, isRequired, level, isReadOnly) => {
        let gotFieldValues = getFieldValue(payload, fieldParentProps);
        let fieldValues = Array.isArray(gotFieldValues) && gotFieldValues.length > 0 ? gotFieldValues : [null];
        const fieldValuesLength = fieldValues.length;
        let fieldType = field.type && Array.isArray(field.type) ? field.type.find((d) => d !== 'null') : field.type;
        let hasFieldValues = fieldValues.filter(fiv => fiv !== null).length > 0;

        let reachedMax = field?.maxItems && fieldValuesLength >= field.maxItems;
        // When SUBMITTALS is enabled and this array's path (or an ancestor) is locked,
        // the "Add X" button and per-item Delete buttons are hidden, and existing items
        // render read-only.
        const arrayPath = parentPropsToPath(fieldParentProps);
        const isArrayLocked = isSubmittalsEnabled && isPathLocked(arrayPath, formHandlers?.lockedPaths);
        return (
            <>
                {field?.items?.properties ? (
                    <Fragment>
                        {fieldValues?.map((item, arrIdx) => (
                            <EFormAccordion
                                key={`${field.title}_${arrIdx}`}
                                testId={`${snakeCase(field?.title)}_${arrIdx}`}
                                title={field.title}
                                subTitle={`${fieldPath} #${arrIdx + 1}`}
                                level={level}
                                arrIdx={arrIdx}
                                fieldType={fieldType ?? "object"}
                                handleAddFunc={handleAddAccordion}
                                handleRemoveFunc={handleRemoveAccordion}
                                isRequired={isRequired}
                                defaultState={isRequired || hasFieldValues}
                                allowDeletion={fieldValues.length > 1 && !isArrayLocked}
                                field={field}
                                parentProps={fieldParentProps}
                                isCreated={hasFieldValues}
                                isReadOnly={isReadOnly || isArrayLocked}
                            >
                                <FormRenderer
                                    schema={field.items}
                                    path={`${fieldPath} #${arrIdx + 1}`}
                                    level={level + 1}
                                    parentProps={[...fieldParentProps, arrIdx]}
                                    payload={payload}
                                    setPayload={setPayload}
                                    isReadOnly={isReadOnly}
                                    previousPayload={previousPayload}
                                    changedFields={changedFields}
                                    isSubmitClicked={isSubmitClicked}
                                    {...formHandlers}
                                />
                            </EFormAccordion>
                        ))}
                        {!isReadOnly && !isArrayLocked && (isRequired || (!isRequired && (gotFieldValues && gotFieldValues.length !== 0))) && (
                            <div className={"eformv2-accordion-thread-container"} data-testid={`${snakeCase(field.title)}__add-accordion`} style={{ cursor: (isReadOnly || reachedMax) ? 'not-allowed' : 'inherit' }}>
                                {level !== 1 && <span className="thread"></span>}
                                <div className={classNames('eformv2-accordion__container', `eformv2-accordion__container-style--action-${level}`, { 'eformv2-disabled': isReadOnly || reachedMax })} onClick={() => handleAddAccordion(fieldParentProps, field.items, reachedMax)}>
                                    <div className={classNames("eformv2-accordion__action-icon", { 'eformv2-disabled': isReadOnly || reachedMax })}><EFormCreateBtn /></div>
                                    <div>
                                        <div className={classNames("eformv2-accordion__main-title--action", { 'eformv2-disabled': isReadOnly || reachedMax })}>
                                            {`Add ${field.title}`}
                                            <InfoIcon content={field.description} />
                                        </div>
                                        <div className={classNames("eformv2-accordion__sub-title--action", { 'eformv2-disabled': isReadOnly || reachedMax })}>{`${fieldPath} #${fieldValuesLength + 1}`}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Fragment>
                ) : renderBaseFields(field, fieldPath, isRequired, fieldParentProps, level, isReadOnly)}
            </>
        );
    };

    const renderBaseFields = (field, fieldPath, fieldType, isRequired, fieldParentProps, level, isReadOnly) => {
        // Create a copy of formHandlers with isSubmitClicked to ensure errors are shown after submit
        const enhancedFormHandlers = {
            ...formHandlers,
            isSubmitClicked
        };
        
        return (
            <div key={fieldPath} style={fieldType === "dynamic-dropdown" ? { flex: `100%` } : { flex: `0 0 calc(50% - ${level > 1 ? level * 0.5 : 0}px - 15px)` }} className={fieldType === "dynamic-dropdown" ? "etr-igpni__model-terms__field" : "eformv2-base-field"}>
                <BaseEForm 
                    title={field.title} 
                    schema={field} 
                    path={fieldPath} 
                    required={isRequired} 
                    parentProps={fieldParentProps} 
                    payload={payload} 
                    setPayload={setPayload} 
                    formHandlers={enhancedFormHandlers} 
                    isReadOnly={isReadOnly} 
                    previousPayload={previousPayload} 
                    changedFields={changedFields} 
                    selectedModelTerms={selectedModelTerms} 
                    setSelectedModelTerms={setSelectedModelTerms} 
                />
            </div>
        );
    };

    /** remove this for threads */
    let isNested = checkIfNested(schema?.properties);
    isNested = false;
    const isLevelValid = level !== 1 && level !== 4;
    const containerClasses = classNames({
        'eformv2-section__container': !isLevelValid,
        [`eformv2-section__container-${level}`]: isLevelValid && !isNested,
        [`eformv2-section__container-${level}-is-nested`]: isLevelValid && isNested
    });

    return (
        <div className={containerClasses}>
            {renderFields(schema?.properties, schema.required || [], path, parentProps, payload, level, isReadOnly)}
        </div>
    );
};

ObjectFieldRenderer.propTypes = {
    testId: PropTypes.string,
    field: PropTypes.object,
    fieldPath: PropTypes.string,
    isRequired: PropTypes.bool,
    fieldParentProps: PropTypes.array,
    level: PropTypes.number,
    payload: PropTypes.object,
    setPayload: PropTypes.func,
    formHandlers: PropTypes.object,
    pathIndex: PropTypes.number,
    handleAddAccordion: PropTypes.func,
    handleRemoveAccordion: PropTypes.func,
    isReadOnly: PropTypes.bool,
    previousPayload: PropTypes.object,
    changedFields: PropTypes.array
};

FormRenderer.propTypes = {
    schema: PropTypes.object,
    path: PropTypes.string,
    level: PropTypes.number,
    parentProps: PropTypes.array,
    setPayload: PropTypes.func,
    payload: PropTypes.object,
    pathIndex: PropTypes.number,
    formHandlers: PropTypes.object,
    isReadOnly: PropTypes.bool,
    previousPayload: PropTypes.object,
    changedFields: PropTypes.array,
    selectedModelTerms: PropTypes.string,
    setSelectedModelTerms: PropTypes.func,
    isSubmitClicked: PropTypes.bool,
};

export default FormRenderer;
